import { useEffect, useId, useRef, useState } from 'react';
import { showBrowseToast } from './communitiesBrowseStore';
import {
  removeCommunitySearch,
  saveCommunitySearch,
  useSavedCommunitySearches,
} from './communitySavedSearches';

interface CommunitiesShareMenuProps {
  /** Human-readable description of the current search, used as the saved-search name. */
  searchLabel: string;
  hasActiveSearch: boolean;
  favoriteIds: string[];
  onApplySavedSearch: (params: URLSearchParams) => void;
}

function pageUrl() {
  return `${window.location.origin}${window.location.pathname}`;
}

function currentSearchQuery() {
  const params = new URLSearchParams(window.location.search);
  params.delete('favorites');
  return params.toString();
}

async function shareOrCopy(url: string, title: string, copiedMessage: string) {
  const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
  const touch = window.matchMedia('(pointer: coarse)').matches;
  if (touch && nav.share) {
    try {
      await nav.share({ title, url });
      return;
    } catch {
      // User dismissed the share sheet; fall back to copying.
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    showBrowseToast(copiedMessage);
  } catch {
    window.prompt('Copy this link', url);
  }
}

function ShareIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 3v12M7 8l5-5 5 5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" strokeLinecap="round" />
    </svg>
  );
}

const itemClass =
  'flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left font-body text-sm text-[rgb(var(--ala-text))] transition hover:bg-[rgb(var(--ala-surface))] disabled:cursor-not-allowed disabled:opacity-50';

export function CommunitiesShareMenu({
  searchLabel,
  hasActiveSearch,
  favoriteIds,
  onApplySavedSearch,
}: CommunitiesShareMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const saved = useSavedCommunitySearches();
  const [query, setQuery] = useState('');
  const alreadySaved = saved.some((item) => item.query === query);

  useEffect(() => {
    if (!open) return;
    setQuery(currentSearchQuery());
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={menuId}
        aria-haspopup="menu"
        onClick={() => setOpen((value) => !value)}
        className="inline-flex min-h-11 items-center gap-2 rounded-md border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] px-3 font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-text))] transition hover:border-[rgb(var(--ala-text))]/30"
      >
        <ShareIcon />
        <span className="hidden 2xl:inline">Save &amp; share</span>
        <span className="sr-only 2xl:hidden">Save and share</span>
      </button>

      {open ? (
        <div
          id={menuId}
          role="menu"
          className="absolute right-0 top-full z-40 mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] p-2 shadow-[0_16px_40px_rgb(var(--ala-primary)/0.16)]"
        >
          <button
            type="button"
            role="menuitem"
            className={itemClass}
            onClick={() => {
              setOpen(false);
              void shareOrCopy(`${pageUrl()}${query ? `?${query}` : ''}`, 'American Landmark communities', 'Link to this search copied');
            }}
          >
            Copy link to this search
          </button>
          <button
            type="button"
            role="menuitem"
            className={itemClass}
            disabled={!hasActiveSearch || alreadySaved}
            onClick={() => {
              if (saveCommunitySearch(searchLabel, query)) showBrowseToast('Search saved');
              setOpen(false);
            }}
          >
            {alreadySaved ? 'This search is saved' : hasActiveSearch ? 'Save this search' : 'Add a filter to save this search'}
          </button>
          <button
            type="button"
            role="menuitem"
            className={itemClass}
            disabled={favoriteIds.length === 0}
            onClick={() => {
              setOpen(false);
              void shareOrCopy(
                `${pageUrl()}?favorites=${encodeURIComponent(favoriteIds.join(','))}`,
                'My favorite American Landmark communities',
                'Link to your favorites copied',
              );
            }}
          >
            Share my favorites{favoriteIds.length > 0 ? ` (${favoriteIds.length})` : ''}
          </button>

          <div className="mt-2 border-t border-[rgb(var(--ala-border))] px-3 pb-1 pt-3">
            <p className="font-heading text-[10px] font-semibold uppercase tracking-wide text-[rgb(var(--ala-text-muted))]">
              Saved searches
            </p>
          </div>
          {saved.length === 0 ? (
            <p className="px-3 pb-2 font-body text-xs text-[rgb(var(--ala-text-muted))]">
              Saved searches appear here on this device.
            </p>
          ) : (
            <ul className="max-h-56 overflow-y-auto">
              {saved.map((item) => (
                <li key={item.id} className="flex items-center gap-1">
                  <button
                    type="button"
                    role="menuitem"
                    className={`${itemClass} min-w-0 flex-1`}
                    onClick={() => {
                      onApplySavedSearch(new URLSearchParams(item.query));
                      setOpen(false);
                    }}
                  >
                    <span className="truncate">{item.label}</span>
                  </button>
                  <button
                    type="button"
                    aria-label={`Delete saved search ${item.label}`}
                    onClick={() => removeCommunitySearch(item.id)}
                    className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-[rgb(var(--ala-text-muted))] transition hover:bg-[rgb(var(--ala-surface))] hover:text-[rgb(var(--ala-text))]"
                  >
                    <span aria-hidden>×</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default CommunitiesShareMenu;
